import axios from 'axios';
import { getBackendUrl } from '../../../../utils/getBackendUrl';

const CREDITS_PATH = 'api/influencer/credits/';
const CHECKOUT_PATH = 'api/influencer/credits/checkout/';
const CONFIRM_PATH = 'api/influencer/credits/confirm/';
const CANCEL_PATH = 'api/influencer/credits/cancel/';
const KEEP_PATH = 'api/influencer/credits/keep/';
const JSON_HEADERS = { 'Content-Type': 'application/json' };

// The credits payload (see the API contract): {enabled: false} when credits
// are off for this creator.
export async function getCredits() {
   const res = await axios({ method: 'get', url: getBackendUrl() + CREDITS_PATH, withCredentials: true });
   return res.data;
}

// Answers {url}, the Stripe-hosted checkout to send her to. A 409 (she already
// has a pack), 503 (packs not on sale here) or any other failure is thrown as
// the axios error, so the caller can read err.response.status and .data.
// A one-off purchase ('topup') sends the purchase key of her tap: the same key
// again is the same purchase (a retry), a new key is a new purchase.
export async function startCheckout(credits, returnPath, purchaseType = 'monthly', purchaseKey = '', journey = null) {
   let extra = {};
   if (purchaseType === 'yearly') extra = { purchase_type: 'yearly' };
   if (purchaseType === 'topup') extra = { purchase_type: 'topup', purchase_key: purchaseKey };
   const res = await axios({
      method: 'post',
      url: getBackendUrl() + CHECKOUT_PATH,
      headers: JSON_HEADERS,
      withCredentials: true,
      data: { credits, return_path: returnPath, ...extra, ...(journey || {}) },
   });
   return res.data;
}

// A new key for one explicit one-off purchase: 8 to 64 of A-Z a-z 0-9 _ - (what the server accepts).
export function newPurchaseKey() {
   try {
      const c = typeof window !== 'undefined' ? window.crypto : null;
      if (c && typeof c.randomUUID === 'function') return c.randomUUID();
      if (c && typeof c.getRandomValues === 'function') {
         return Array.from(c.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
      }
   } catch (e) {
      // No crypto: the fallback below.
   }
   return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12).padEnd(10, '0')}`;
}

// Answers the payload once the pack's first month is granted (idempotent with
// the webhook).
export async function confirmCheckout(sessionId) {
   const res = await axios({
      method: 'post',
      url: getBackendUrl() + CONFIRM_PATH,
      headers: JSON_HEADERS,
      withCredentials: true,
      data: { session_id: sessionId },
   });
   return res.data;
}

// Ends HER monthly credits at the end of the period she paid for (the credits already granted stay). Answers the
// payload, with monthly.pack.cancel_at set. A 404 {error: 'no_pack'} (she has none) is thrown as the axios error.
export async function cancelPack() {
   const res = await axios({
      method: 'post',
      url: getBackendUrl() + CANCEL_PATH,
      headers: JSON_HEADERS,
      withCredentials: true,
      data: {},
   });
   return res.data;
}

// Undoes a pending cancel. Answers the payload, with monthly.pack.cancel_at null again. 404 no_pack as above.
export async function keepPack() {
   const res = await axios({
      method: 'post',
      url: getBackendUrl() + KEEP_PATH,
      headers: JSON_HEADERS,
      withCredentials: true,
      data: {},
   });
   return res.data;
}

// Cancel or keep answers 404 {error: 'no_pack'} when she has no pack (it ended, or was cancelled in another tab).
export function isNoPack(err) {
   const response = err && err.response;
   return Boolean(response && response.status === 404 && response.data && response.data.error === 'no_pack');
}

// A job that cannot start answers 402 {error: 'insufficient_credits', agent, credits}.
export function isInsufficientCredits(err) {
   const response = err && err.response;
   return Boolean(response && response.status === 402 && response.data && response.data.error === 'insufficient_credits');
}

export function creditsFromError(err) {
   return (err && err.response && err.response.data && err.response.data.credits) || null;
}
